// FulfillmentIntakePanel -- Slice 1D.
//
// Form queue split into three collapsible sub-sections plus an
// approved tail. Each row exposes the relevant action button:
//
//   pending          -> [Fill]      (lot worker)
//   awaiting_review  -> [Review]    (admin/owner)
//   work_in_progress -> [Mark done] (lot worker, once Tier-2 work is finished)
//   approved         -> read-only
//
// An admin-only "Templates" sub-tab is rendered inline at the
// bottom (Slice 1 simplification per planning doc §4 row 6).

import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { useFulfillmentIntake } from "./useFulfillmentIntake";
import { FulfillmentIntakeFormFillModal } from "./FulfillmentIntakeFormFillModal";
import { FulfillmentIntakeFormReviewModal } from "./FulfillmentIntakeFormReviewModal";
import { FulfillmentIntakeTemplatesSubTab } from "./FulfillmentIntakeTemplatesSubTab";
import {
  markFulfillmentIntakeWorkComplete,
  type FulfillmentIntakeForm,
  type FulfillmentIntakeTemplate,
} from "@/lib/fulfillment/fulfillmentApi";

type ActionKind = "fill" | "review";

function FormRow({
  form,
  action,
  onClick,
  onMarkDone,
}: {
  form: FulfillmentIntakeForm;
  action: ActionKind | null;
  onClick: () => void;
  onMarkDone?: () => void;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2">
      <div>
        <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
          Unit <code>{form.mfg_unit_id.slice(0, 8)}</code>
        </div>
        <div className="text-xs text-gray-500 dark:text-zinc-400">
          status <code>{form.status}</code>
          {form.due_at ? ` · due ${form.due_at.slice(0, 10)}` : ""}
          {form.service_request_id ? " · Service lane" : ""}
        </div>
      </div>
      <div className="flex gap-2">
        {action ? (
          <button
            type="button"
            {...(action === "fill"
              ? { [FIREFLY_TARGET_ATTR]: "ff-intake-fill" }
              : action === "review"
                ? { [FIREFLY_TARGET_ATTR]: "ff-intake-review" }
                : {})}
            onClick={onClick}
            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium dark:border-zinc-700 dark:bg-zinc-900"
          >
            {action === "fill" ? "Fill" : "Review"}
          </button>
        ) : null}
        {onMarkDone ? (
          <button
            type="button"
            onClick={onMarkDone}
            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium dark:border-zinc-700 dark:bg-zinc-900"
          >
            Mark work done
          </button>
        ) : null}
      </div>
    </li>
  );
}

function Section({
  title,
  count,
  fireflyTargetId,
  children,
}: {
  title: string;
  count: number;
  fireflyTargetId?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(count > 0);
  return (
    <div
      {...(fireflyTargetId ? { [FIREFLY_TARGET_ATTR]: fireflyTargetId } : {})}
      className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
          {title} <span className="text-gray-500 dark:text-zinc-400">({count})</span>
        </span>
        <span className="text-xs text-gray-500 dark:text-zinc-400">{open ? "Hide" : "Show"}</span>
      </button>
      {open ? <div className="mt-2">{children}</div> : null}
    </div>
  );
}

export function FulfillmentIntakePanel() {
  const auth = useAuth();
  const isAdmin = auth.role === "admin" || auth.role === "owner" || auth.role === "dev";
  const m = useFulfillmentIntake();
  const [active, setActive] = useState<
    | { kind: "fill"; form: FulfillmentIntakeForm }
    | { kind: "review"; form: FulfillmentIntakeForm }
    | null
  >(null);
  const [markErr, setMarkErr] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);

  async function markDone(formId: string) {
    setMarkErr(null);
    const r = await markFulfillmentIntakeWorkComplete({ form_id: formId });
    if (!r.ok) {
      setMarkErr(r.error);
      return;
    }
    void m.refresh();
  }

  function findTemplate(form: FulfillmentIntakeForm): FulfillmentIntakeTemplate | null {
    if (!form.template_id) return null;
    return m.templates.find((t) => t.id === form.template_id) ?? null;
  }

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">
          Fulfillment — Intake forms
        </h2>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void m.refresh()}
            disabled={m.busy}
            className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          >
            {m.busy ? "Refreshing…" : "Refresh"}
          </button>
          {isAdmin ? (
            <button
              type="button"
              onClick={() => setShowTemplates((v) => !v)}
              className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
            >
              {showTemplates ? "Hide templates" : "Templates"}
            </button>
          ) : null}
        </div>
      </div>

      {(m.error || markErr) ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.error || markErr}
        </div>
      ) : null}

      <div {...{ [FIREFLY_TARGET_ATTR]: "ff-intake-queue" }} className="space-y-4">
        <Section
          title="Needs filling"
          count={m.buckets.needsFilling.length}
          fireflyTargetId="ff-intake-needs-filling"
        >
          <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
            {m.buckets.needsFilling.map((f) => (
              <FormRow
                key={f.id}
                form={f}
                action="fill"
                onClick={() => setActive({ kind: "fill", form: f })}
              />
            ))}
          </ul>
        </Section>

        <Section
          title="Needs review"
          count={m.buckets.needsReview.length}
          fireflyTargetId="ff-intake-needs-review"
        >
          <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
            {m.buckets.needsReview.map((f) => (
              <FormRow
                key={f.id}
                form={f}
                action={isAdmin ? "review" : null}
                onClick={() => isAdmin && setActive({ kind: "review", form: f })}
              />
            ))}
          </ul>
        </Section>

        <Section title="Needs work" count={m.buckets.needsWork.length}>
          <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
            {m.buckets.needsWork.map((f) => (
              <FormRow
                key={f.id}
                form={f}
                action={null}
                onClick={() => undefined}
                onMarkDone={() => void markDone(f.id)}
              />
            ))}
          </ul>
        </Section>

        <Section title="Approved" count={m.buckets.approved.length}>
          <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
            {m.buckets.approved.map((f) => (
              <FormRow key={f.id} form={f} action={null} onClick={() => undefined} />
            ))}
          </ul>
        </Section>
      </div>

      {isAdmin && showTemplates ? (
        <FulfillmentIntakeTemplatesSubTab templates={m.templates} onRefresh={m.refresh} />
      ) : null}

      {active?.kind === "fill" ? (
        <FulfillmentIntakeFormFillModal
          form={active.form}
          template={findTemplate(active.form)}
          onClose={() => setActive(null)}
          onSaved={() => void m.refresh()}
        />
      ) : null}
      {active?.kind === "review" ? (
        <FulfillmentIntakeFormReviewModal
          form={active.form}
          template={findTemplate(active.form)}
          onClose={() => setActive(null)}
          onSaved={() => void m.refresh()}
        />
      ) : null}
    </div>
  );
}
